import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    TemplateRef,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventDetailsColumnType, EventModel, EventOptionGroup, FootballStats } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, GridConfig as MsGrid, Sitecore, StylingGridConfiguration } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { EventMediaService } from '@frontend/sports/grid/core/feature/event-media';
import {
    Column,
    EventGroup,
    GridBreakpointSize,
    GridEvent,
    GridLayout,
    GridModel,
    GridSorting,
    Group,
} from '@frontend/sports/grid/core/feature/model';
import { GridActions, IGridRootState, getEvents } from '@frontend/sports/grid/core/feature/store';
import { GridTrackingService } from '@frontend/sports/grid/core/feature/tracking';
import { EventLinkConfig } from '@frontend/sports/navigation/core/feature/url-helper';
import { TRACKING_GAMEDETAILS, TRACKING_NO_GAMEDETAILS, TrackingSource } from '@frontend/sports/tracking/feature';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { flatMap, flatten, isEqual, orderBy } from 'lodash-es';
import { Subscription, distinctUntilChanged } from 'rxjs';

import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { LiveFavouriteFixturesService } from '../favourites/live-favourites-fixtures.service';
import { GridBreakpointService } from './grid-breakpoint.service';
import { HeaderGroup } from './header/grid-header.component';

@Component({
    selector: 'ms-grid',
    templateUrl: 'grid.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [GridTrackingService],
    standalone: false,
})
export class GridComponent implements OnInit, OnChanges {
    @Input() model: GridModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() sortingTracking?: string;
    @Input() title?: string;
    @Input() containerRatio?: number;
    @Input() hideHeader = false;
    @Input() hideTitle = false;
    @Input() hideFavourites = false;
    @Input() hideMarketHeader = false;
    @Input() calendarInfoDisplay = false;
    @Input() allowHeaderCollapse = true;
    @Input() optionGroupTemplate: TemplateRef<any>;
    @Input() hideMarketSwitcher = false;
    @Input() setMarketSwitcherHeight = false;
    @Input() offerBadgeTemplate?: TemplateRef<any>;
    @Input() selectedTabSportId?: number;
    @Input() iconTemplate?: TemplateRef<any>;
    @Input() showOptionname: boolean;
    @Input() collapseSportHeaders = false;
    @Input() showAllWagers: boolean;
    @Input() hideHeaderRegion = false;
    @Input() hideGroupCount = false;
    @Input() betBuilderEpcotEnabled = false;
    @Input() betDetails = '';
    @Input() popperAppendTo = 'body';
    @Input() trackCalenderTitle?: string;
    @Input() linkConfig: EventLinkConfig = {
        emptyText: this.sitecore.eventGrid.emptyMarketLinkText,
    };

    @Input() gridHeaderOverride?: string;
    @Input() hideFallbackIcon = false;
    @Input() competitionFallbackIcon?: string;

    @Output() eventClick = new EventEmitter<{ event: EventModel; index: number }>();
    @Output() groupToggle = new EventEmitter<EventGroup>();
    @Output() moreBetsClicked = new EventEmitter<EventModel>();
    @Output() statsClicked = new EventEmitter<EventModel>();
    @Output() toggleAccordionClick = new EventEmitter<boolean>();

    baseCount: number;
    swipeToggle = false;
    favouriteToggle = false;
    useColoredIcon = false;
    showGridPillsHeader = false;
    showRadioInputSortSelectorInHeader = false;
    empty: boolean;
    emptyHeader = true;
    emptyMessage: string;
    columns: Column[] = [];
    groups: Group[] = [];
    layout: GridLayout;
    layoutType = GridLayout;
    promotedFixtureIdsMap: StringDictionary<string> = {};
    showParticipantImage: boolean;
    expandedSport: { [id: number]: boolean } = {};
    eventCount: number;

    private mediaType = EventDetailsColumnType;
    private breakpointSnapshot: GridBreakpointSize = { columns: 1, default: 1 };
    private breakpointSubscription: Subscription;
    private groupsChanged = false;
    private epcotEnabled = false;
    @HostBinding('class') className = 'grid grid-group-x1';

    @HostBinding('class.bold-participant-names') get showParticipantBoldName(): boolean {
        return this.stylingGridConfig.showParticipantBoldName;
    }

    @HostBinding('class.grid-redesign') get showEnhancedGridView(): boolean {
        return this.epcotEnabled && this.msGrid.is2way3wayEnabled;
    }

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private breakpointService: GridBreakpointService,
        private element: ElementRef,
        private changeRef: ChangeDetectorRef,
        private favouritesService: LiveFavouriteFixturesService,
        private store: Store<IGridRootState>,
        private msGrid: MsGrid,
        private deviceService: DeviceService,
        private stylingGridConfig: StylingGridConfiguration,
        private eventMediaService: EventMediaService,
        epcotConfig: EpcotConfig,
        promotionsProvider: CrmPromotionProviderService,
        private nativeAppService: NativeAppService,
        @Optional() @Inject(GRID_CONFIG) gridConfig: GridConfig | null,
    ) {
        if (!gridConfig?.hidePromotionBadgeInGrid) {
            promotionsProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((ids) => this.promotionMapChanged(ids));
        }
        this.showParticipantImage = msGrid.showParticipantLogoOrImage;

        if (gridConfig) {
            this.useColoredIcon = gridConfig.useColoredIcon;
        }
        this.epcotEnabled = epcotConfig.isEnabled;
        this.showRadioInputSortSelectorInHeader = this.epcotEnabled;
    }

    ngOnInit() {
        this.deviceService.orientation.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((orientation) => {
            this.setGridPillsHeaderVisibility(orientation);
            this.changeRef.markForCheck();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const model = changes.model;

        if (!model) {
            return;
        }

        const current: GridModel = model.currentValue || {};
        const previous: GridModel = model.previousValue || {};
        if (this.collapseSportHeaders) {
            this.eventCount = flatMap(model.currentValue.groups, (group) => group.events)?.length;
            if (changes.model.firstChange) {
                this.expandedSport[model.currentValue.sport.id] = true;
            }
        }

        if (current !== previous) {
            this.setFavouriteAvailability();

            const sourceChanged = current.id !== previous.id;
            const layoutChanged = current.layout !== previous.layout || sourceChanged;
            const columnChanged = !isEqual(current.columns, previous.columns);

            if (layoutChanged) {
                this.setBreakpointService();
            }

            if (layoutChanged || columnChanged) {
                this.setModel(columnChanged);
            }

            if (sourceChanged) {
                this.setEmpty(true);
                // default animation applicable only for golf when live betting golf tab has been clicked
                if (this.selectedTabSportId === SportConstant.Golf) this.setAnimation(current);
            } else if (columnChanged && this.groupsChanged) {
                this.groupsChanged = false;
                this.setEmpty();
            }

            if (this.collapseSportHeaders && sourceChanged) {
                this.expandedSport[model.currentValue.sport.id] = true;
            }
        }
    }

    groupToggled(group: EventGroup): void {
        this.store.dispatch(GridActions.setGroupCollapse({ payload: { id: this.model.id, groupId: group.id } }));
        this.groupToggle.next(group);
    }

    moreToggled(more: boolean): void {
        this.store.dispatch(GridActions.setMoreCollapse({ gridId: this.model.id }));
    }

    changeSorting(sorting: GridSorting): void {
        this.store.dispatch(GridActions.changeSorting({ payload: { id: this.model.id, sorting } }));
    }

    groupChanged(event: HeaderGroup): void {
        const active = event.column.groups.find((group) => group.active);
        const payload = { id: this.model.id, columnId: event.column.id, groupId: event.group.id };

        if (active && active.extended !== event.group.extended) {
            this.store.dispatch(
                GridActions.setActiveGroupType({
                    payload: {
                        ...payload,
                        extended: event.group.extended,
                    },
                }),
            );

            return;
        }

        this.groupsChanged = true;
        this.store.dispatch(GridActions.setActiveGroup({ payload }));
    }

    eventCollapsed(id: string, optionGroupId?: string): void {
        this.store.dispatch(GridActions.collapseEvents({ payload: { id: this.model.id, eventId: id, optionGroupId } }));
    }

    eventClicked(event: GridEvent, index: number): void {
        this.eventClick.emit({ event, index });
    }

    trackingGameDetails(event: GridEvent, trackingType: string, index: number): TrackingSource {
        const source = this.tracking + '/' + trackingType;

        return {
            source,
            position: index,
            gameDetails: this.getTrackingGameDetails(event),
            moduleType: this.trackingModuleType,
            calendarTitle: this.trackCalenderTitle,
        };
    }

    accordionCollapsed(isAccordionCollapsed: boolean) {
        this.toggleAccordionClick.emit(isAccordionCollapsed);
    }

    private getTrackingGameDetails(event: GridEvent): string {
        let trackingGameDetails = TRACKING_NO_GAMEDETAILS;
        if (event.scoreboard.statistics && event.sport.id === SportConstant.Soccer && this.msGrid.showRedCardsInSoccer) {
            const stas = event.scoreboard.getStats<FootballStats>();
            if (stas.redcards?.totalPoints?.participant1 || stas.redcards?.totalPoints?.participant2) {
                trackingGameDetails = TRACKING_GAMEDETAILS;
            }
        }

        return trackingGameDetails;
    }

    trackEvent(index: number, event: GridEvent): string {
        return `${event.id}-${event.favourited === undefined}`;
    }

    trackGroup(index: number, group: EventGroup): number {
        return group.id;
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.promotedFixtureIdsMap = promoMap.fixtures;
        this.changeRef.markForCheck();
    }

    private setBreakpointService(): void {
        if (this.breakpointSubscription) {
            this.breakpointSubscription.unsubscribe();
        }

        this.breakpointSubscription = this.breakpointService
            .forGrid(this.element, this.model.layout)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((snapshot) => {
                this.breakpointSnapshot = snapshot;
                this.setFavouriteAvailability();
                this.setModel(true);
                this.changeRef.markForCheck();
            });
    }

    private setModel(columnChange: boolean): void {
        if (columnChange) {
            const visible = orderBy(this.model.columns, [(column) => column.enabled], ['desc']).slice(0, this.breakpointSnapshot.columns);

            this.columns = this.model.columns.filter((column) => visible.indexOf(column) !== -1);
            this.groups = this.columns.filter((column) => column.enabled).map((column) => column.groups.find((group) => group.active)!);
        }

        this.layout = this.model ? this.model.layout : GridLayout.Default;

        if (this.groups.some((group) => group.extended)) {
            this.layout = GridLayout.Hybrid;
        }

        this.emptyMessage = this.groups.length === 1 ? this.sitecore.eventGrid.NoEventsWithMarket : this.sitecore.eventGrid.NoEventsWithMarkets;

        const classes = ['grid', `grid-group-x${this.columns.length || 1}`];
        const first = this.groups[0];
        const condensed =
            this.groups.length === 1 &&
            this.breakpointSnapshot.columns === 1 &&
            (first.balancedMarket || (first.options && first.options.length === 2) || first.id === 'main');
        if (condensed) {
            classes.push('grid-condensed');
        }
        if (this.layout === GridLayout.SixPack) {
            classes.push(`base-count-x${this.breakpointSnapshot.default}`);
            this.baseCount = this.breakpointSnapshot.default;
        }

        if (this.favouriteToggle) {
            classes.push('grid-live-favourite');
        }

        if (this.hideFavourites) {
            classes.push('grid-hide-favourite');
        }

        this.className = classes.join(' ');
        if (this.nativeAppService.isTerminal) {
            if (this.model.columns.length === 0) {
                this.hideHeader = true;
            }
        }
    }

    private setEmpty(reset: boolean = false): void {
        if (reset || this.layout === GridLayout.Hybrid) {
            this.empty = false;

            return;
        }

        const groupIdWithFallbackGridIds: string[] = [];
        this.layout === GridLayout.SixPack
            ? flatten(this.groups.map((group) => group.childGroups || []))
            : this.groups.map((group) => {
                  groupIdWithFallbackGridIds.push(group.id);
                  if (group?.fallbackGridGroupIds?.length) {
                      group.fallbackGridGroupIds.map((fallbackGridId) => groupIdWithFallbackGridIds.push(fallbackGridId));
                  }
              });

        const events = getEvents(this.model);

        const hasGroup = (optionGroup: EventOptionGroup): boolean =>
            optionGroup.online &&
            optionGroup.options.some((option) => option.online) &&
            groupIdWithFallbackGridIds.some((group) => optionGroup.hasGroup(group));

        const hasOptionGroup = (optionGroups: EventOptionGroup[]): boolean => optionGroups.some(hasGroup);

        this.empty = !events.some((event) => hasOptionGroup(event.optionGroups));
    }

    private setAnimation(current: GridModel) {
        // find top event for sport which had animation is enabled.
        const gridEvent = flatMap(this.model.groups, (group) => group.events)?.find((event) =>
            this.eventMediaService.hasAnimation(event),
        ) as GridEvent;
        if (gridEvent) {
            this.eventMediaService.activateMedia(this.mediaType.Animation, gridEvent, this.model.media);
            this.statsClicked.emit();
        }
    }

    private setFavouriteAvailability(): void {
        this.swipeToggle = !this.hideFavourites && this.favouritesService.isSwipeFavouriteEnabled();
        this.favouriteToggle = !this.hideFavourites && this.favouritesService.isLiveFavouriteEnabled();
    }

    getAutomationAttributes(event: GridEvent): { id: string; type: string } {
        return {
            id: event.id,
            type: 'grid-event',
        };
    }

    private setGridPillsHeaderVisibility(currentOrientation: string): void {
        this.showGridPillsHeader =
            this.msGrid.isMarketPillsEnabled && this.deviceService.isMobilePhone && currentOrientation !== 'landscape' && !this.hideMarketSwitcher;
    }
}
